import * as tf from "@tensorflow/tfjs-core";
import "@tensorflow/tfjs-backend-wasm";
import "@tensorflow/tfjs-backend-webgl";

/* ================== CONSTANTS ================== */

const CLASS_LABELS = [
  "",
  "front_bumper",
  "front_door",
  "back_door",
  "front_glass",
  "fender",
  "qtr_panel",
  "window_glass",
  "running_board",
  "side_view_mirror",
  "headlight",
  "wheel",
  "back_bumper",
  "dicky",
  "back_glass",
  "taillight",
  "hood",
];

const CLASS_THRESHOLDS = new Float32Array(17).fill(0.7);

const customThresholds = {
  8: 0.3,
  5: 0.4,
  6: 0.4,
  2: 0.6,
  7: 0.4,
  11: 0.45,
  14: 0.4,
};

Object.entries(customThresholds).forEach(
  ([idx, val]) => (CLASS_THRESHOLDS[idx] = val)
);

/* ================== QUANTIZATION ================== */

const S_SCORE = 0.003807874396443367;
const S_BOX = 0.007213558070361614;
const Z_BOX = 45;
const S_CLASS = 0.05882352963089943;
const S_NUM = 0.09803921729326248;

/* ================== LOAD MODEL ================== */

export async function loadCarPartModel() {
  await tf.setBackend("webgl");
  await tf.ready();

  const model = await window.tflite.loadTFLiteModel("/model_int8.tflite");
  return model;
}

/* ================== PREPROCESS ================== */
/* IMPORTANT: pass a 320x320 canvas instead of full video */

export const preprocessVideo = (canvas) => {
  return tf.tidy(() => {
    return tf.browser
      .fromPixels(canvas)
      .expandDims(0)
      .cast("int32");
  });
};

/* ================== INFERENCE ================== */

export function runCarPartDetection(model, inputTensor) {

  const result = model.predict(inputTensor);
  const outputs = Array.isArray(result) ? result : [result];
  const tensors = Object.values(outputs[0] || outputs);

  if (tensors.length !== 4) {
    cleanupTensors(outputs);
    inputTensor.dispose();
    return [];
  }

  const [scoresT, boxesT, numT, classT] = tensors;

  /* Faster synchronous reads */
  const scoresData = scoresT.dataSync();
  const boxesData = boxesT.dataSync();
  const classData = classT.dataSync();
  const numData = numT.dataSync();

  const count = Math.min(Math.round(numData[0] * S_NUM), scoresData.length);
  const detections = [];

  for (let i = 0; i < count; i++) {

    const score = scoresData[i] * S_SCORE;
    if (score < 0.05) continue;

    const classId = Math.round(classData[i] * S_CLASS) + 1;
    const threshold = CLASS_THRESHOLDS[classId] || 0.7;

    if (score >= threshold && CLASS_LABELS[classId]) {

      const b = i * 4;

      const ymin = (boxesData[b] - Z_BOX) * S_BOX;
      const xmin = (boxesData[b + 1] - Z_BOX) * S_BOX;

      detections.push({
        bbox: [
          xmin,
          ymin,
          (boxesData[b + 3] - Z_BOX) * S_BOX - xmin,
          (boxesData[b + 2] - Z_BOX) * S_BOX - ymin,
        ],
        class: classId,
        label: CLASS_LABELS[classId],
        score,
      });
    }
  }

  cleanupTensors(outputs);
  inputTensor.dispose();

  return detections;
}

/* ================== CLEANUP ================== */

function cleanupTensors(outputs) {
  outputs.forEach((t) => {
    if (t && t.dispose) t.dispose();
  });
}